/**
 * Which of a venue's own images the showcase (`/locale/[slug]`) hands to
 * WhatsApp, LinkedIn and every other Open Graph scraper.
 *
 * Until now the showcase declared `twitter:card = summary_large_image` and
 * emitted no image at all, so a restaurateur sharing their own page got whatever
 * asset the scraper found on it. The photos were already there, on 195 of 200
 * showcases sampled on 02/09/2026: the page only ever looked at
 * `restaurant.meta_image`, a field nothing populates today (1 showcase in those
 * 200 had one).
 *
 * `meta_image` still wins, and that order is the whole point.
 * `RestWorld.Employers.SEO.ImageComposer` in core-services already composes
 * 1200x630 images from `restaurant.pictures[0]` under a 307 KB cap, and already
 * writes them to `meta_image_url` for job ADS. The day the same runs for venues,
 * those images land in `meta_image` and are preferred here with no change to
 * this file: the gallery photo becomes the fallback for showcases the job has
 * not reached. See `docs/claude/venue-og-image.md` for why that is the real home
 * for this and why it is not done yet.
 *
 * This picks the image and says what kind it is; it does not size it.
 * `imageVariant` is applied at the call site, the same way `venue-info` does for
 * the gallery, because reaching the `@restworld/utils-common` barrel from here
 * drags the whole authentication graph into jest.
 */

export type VenueOgImageSource = {
  metaImage?: string | null;
  pictures?: string[] | null;
};

/**
 * The two kinds are kept apart because only one of them may be resized.
 *
 * A gallery photo is a raw upload and goes through `imageVariant`, like every
 * other user-uploaded asset on the page. `meta_image` is the opposite: it is an
 * image made to be an Open Graph image, already 1200x630 on the one showcase
 * that has one today and 1200x630 by construction the day `ImageComposer` fills
 * it for venues. Asking for a derivative of it would be asking for a different
 * file than the one that was built to be right, and for a `meta_image` stored
 * without the `_orig.jpg` suffix it would route a perfectly good URL through the
 * lazy variant endpoint for nothing.
 */
export type VenueOgImage = { kind: 'meta-image'; url: string } | { kind: 'gallery-photo'; url: string };

export function venueOgImage(source?: VenueOgImageSource | null): VenueOgImage | undefined {
  const metaImage = clean(source?.metaImage);
  if (metaImage) return { kind: 'meta-image', url: metaImage };

  const photo = (source?.pictures ?? []).map(clean).find(Boolean);
  return photo ? { kind: 'gallery-photo', url: photo } : undefined;
}

/**
 * Empty and whitespace-only both mean "no image". `minifyRestaurantData` maps an
 * absent field to `null`, and a blank string reaching `og:image` would be a tag
 * that looks present in the HTML and resolves to nothing, which is the exact
 * class of failure this whole change exists to end.
 */
function clean(value?: string | null): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}
